import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureBatchLinkedService } from 'app/model/linkedservice/azure-batch.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { Credential, AuthenticationType, CredentialTypeOptions, ITridentResource, ConnectionAuthKind } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureBatchLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                accountName: '*',
                accessKey: 'secret',
                batchUri: '*',
                poolName: '*',
                linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
                encryptedCredential: '*',
                credential: '*'
            }
        }
    }
})
export class AzureBatchLinkedServiceAdapter extends LinkedServiceAdapter<AzureBatchLinkedService> {

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureBatchLinkedService): Promise<void> {
        let finalAuthType: string = AuthenticationType.AccountKey;
        model.isAuthRefMethodCredential = false;
        if (model.credential) {
            if (model.credential.getType() === CredentialTypeOptions.UserAssignedManagedIdentity) {
                model.credential = null;
            } else {
                model.isAuthRefMethodCredential = true;
            }
        }

        // if accessKey is not provided keep the type MI in any case
        if (!model.accessKey) {
            finalAuthType = AuthenticationType.ManagedServiceIdentity;
        }

        model.authenticationType = finalAuthType;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Partial<Model.AzureBatchLinkedServiceTypeProperties> => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        const path = datasourceReference.path;
        const pathProperties = path.split(';');
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Key:
                return {
                    accountName: pathProperties[0],
                    batchUri: pathProperties[1],
                    poolName: pathProperties[2],
                    accessKey: passwordOrAKVtoSecretBase('dummyKey', this.context)   //fake accessKey
                };
            default:
                return undefined;
        }
    };

    protected modelConstructor(): new () => AzureBatchLinkedService {
        return AzureBatchLinkedService;
    }

    protected model(): IModel {
        return AzureBatchLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureBatchLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureBatchLinkedService;

        properties.type = Metamodel.modelFor(AzureBatchLinkedService).name;
        properties.typeProperties = {
            batchUri: linkedService.batchUri,
            poolName: linkedService.poolName,
            accountName: linkedService.accountName,
            linkedServiceName: linkedService.azureStorageLinkedService ?
                ReferenceAdapter.toResource(linkedService.azureStorageLinkedService, this.context) as Model.LinkedServiceReference :
                undefined
        };

        // This should empty uami credentials if provided. We would need to add more checks when we decide to add Uami support
        if (linkedService.credential && linkedService.credential.getType() === CredentialTypeOptions.UserAssignedManagedIdentity) {
                properties.typeProperties.credential = null;
        }

        if (linkedService.authenticationType === AuthenticationType.AccountKey) {
            properties.typeProperties.accessKey = passwordOrAKVtoSecretBase(linkedService.accessKey, this.context);
        } else if (linkedService.credential) {
            // if access key not present this should be mi, check if credential exists
            properties.typeProperties.credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureBatchLinkedService): Promise<AzureBatchLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureBatchLinkedService;
        linkedService.batchUri = properties.typeProperties.batchUri;
        linkedService.poolName = properties.typeProperties.poolName;
        linkedService.accountName = properties.typeProperties.accountName;

        if (properties.typeProperties.accessKey || properties.typeProperties.encryptedCredential) {
            linkedService.accessKey = secretBaseToPasswordOrAKV(properties.typeProperties.accessKey, true, this.context);
            linkedService.authenticationType = AuthenticationType.AccountKey;
        } else {
            linkedService.authenticationType = AuthenticationType.ManagedServiceIdentity;
        }

        if (properties.typeProperties.credential) {
            // if access key not present this should be mi, check if credential exists
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
        }

        if (properties.typeProperties.linkedServiceName != null) {
            linkedService.azureStorageLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                properties.typeProperties.linkedServiceName, this.context);
        }
        return linkedService;
    }
}
